import { Box, Flex, Grid, Text } from '@lego/klik-ui';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { TextHeader } from '@/components/common';
import type { DetailSectionProps } from '@/components/common/details/DetailsBox';
import { EditableField } from '@/components/common/EditableField';
import { DocumentationLink } from '../DocumentationLink';

type Props = {
  section: DetailSectionProps;
  editableFields: string[];
  editedValue: string;
  editingKey: string | null;
  isSaving: boolean;
  onStartEdit: (key: string, currentValue: ReactNode) => void;
  onCancel: () => void;
  onSave: (key: string, value?: string) => Promise<void>;
  onInputChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => void;
  onValueChange: (value: string) => void;
};

export const EditableDetailsSection = ({
  section,
  editableFields,
  editedValue,
  editingKey,
  isSaving,
  onStartEdit,
  onCancel,
  onSave,
  onInputChange,
  onKeyDown,
  onValueChange,
}: Props) => {
  const hasBorder = Boolean(section.title);

  return (
    <Box
      border={hasBorder ? '2px solid' : undefined}
      borderColor={hasBorder ? 'lightgrey' : undefined}
      borderRadius={hasBorder ? 'md' : undefined}
      mb={6}
      p={hasBorder ? 5 : undefined}
    >
      {section.title && <TextHeader tagLevel="h4" title={section.title} />}
      <Grid gap={4} templateColumns="repeat(auto-fill, minmax(300px, 1fr))">
        {Object.entries(section.items).map(([key, value]) => (
          <Grid.Item alignContent="start" key={key}>
            {editableFields.includes(key) ? (
              <EditableField
                editableValue={value.editableValue}
                editedValue={editedValue}
                fieldKey={key}
                isEditing={editingKey === key}
                isSaving={isSaving}
                onCancel={onCancel}
                onInputChange={onInputChange}
                onKeyDown={onKeyDown}
                onSave={onSave}
                onStartEdit={onStartEdit}
                onValueChange={onValueChange}
                renderEditor={value.renderEditor}
                value={value.body}
              />
            ) : (
              <Box>
                <Flex direction="row" gap={1}>
                  <Text fontWeight="medium">{key}</Text>
                  <DocumentationLink url={value.documentationLink} />
                </Flex>
                <Box>{value.body}</Box>
              </Box>
            )}
          </Grid.Item>
        ))}
      </Grid>
    </Box>
  );
};
